
import { create } from 'zustand';
import { exportStageToBlob } from './utils/exportSurface';
const SURFACE_KEYS = ['front', 'frontSilver', 'back', 'backSilver'];

const deepClone = (v) => JSON.parse(JSON.stringify(v ?? []));

const generateId = () =>
  `obj-${Date.now()}-${Math.floor(Math.random() * 10000)}`;

  const cloneObjectForSilver = (obj) => ({
  ...structuredClone(obj),   // or JSON.parse(JSON.stringify(obj))
  id: generateId(),           // MUST be new
});

const stripRuntimeProps = (obj) => {
  const { _qrImage, _qrCacheKey, ...rest } = obj;
  return rest;
};



const splitBackground = (objects) => {
  if (objects.length > 0 && objects[0]?.isBackground) {
    return {
      bg: objects[0],
      rest: objects.slice(1)
    };
  }
  return {
    bg: null,
    rest: objects
  };
};

const getDefaultSurfaces = () => ({
  front: [],
  frontSilver: [],
  back: [],
  backSilver: [],
});

const normalizeSurfaceKey = (k) =>
  SURFACE_KEYS.includes(k) ? k : 'front';

const isBackground = (obj) => obj?.isBackground === true;


const useDesignerStore = create((set, get) => ({

  exportCurrentSurface: async (format = 'png') => {
  const { stageRef, scale, cardWidth, cardHeight } = get();

  if (!stageRef?.current) return null;

  return await exportStageToBlob({
    stage: stageRef.current,
    scale,
    cardWidth,
    cardHeight,
    format
  });
},
  // 
  // exportCurrentSurface: null,
  // setExporter: (fn) => set({ exportCurrentSurface: fn }),

  fontOptions: [],
  fontsReady: false,
  loadFonts: async () => {
    const res = await fetch(
      "https://every722.s3.amazonaws.com/everyid-assets/fonts/fonts.json"
    );
    const data = await res.json();
    const families = data.fonts.map(f => f.family);
    set({ fontOptions: families });
    await document.fonts.ready;
    set({ fontsReady: true });
  },
  stageRef: null,
  setStageRef: (ref) => set({ stageRef: ref }),

  objects: [],
  surfaces: getDefaultSurfaces(),
  activeSurface: 'front',

  selectedIds: [],
  history: [],
  redoStack: [],
  clipboard: [],
  gridSize:30,
  showGrid: true,
  isSnapToGridEnabled: true,
  isAspectRatioLocked: false,

  selectedObjectId: null,
  setSelectedObjectId: (id) => set({ selectedObjectId: id }),
  flushTransforms: null,
  setFlushTransforms: (fn) => set({ flushTransforms: fn }),
  // Design metadata
  cardId: null,
  orgId: '',
  name: '',
  description: '',
  // setName: (name) => set({ name }),
  // setOrgId: (orgId) => set({ orgId }),
  // setDescription: (description) => set({ description }),
  // datasetName: '',
  cardSettings: {
    size: 'cr80',
    format: 'landscape',
    sides: 'Simplex',
    display: 'Side 1'
  },
  stageKey: 0,
  selectedTemplateId: null,
  setSelectedTemplateId: (templateId) => set({ selectedTemplateId: templateId }),


  // Core state setters
  setSelectedIds: (ids) => set({ selectedIds: ids }),
  toggleSelectedId: (id) => set((state) => ({
    selectedIds: state.selectedIds.includes(id)
      ? state.selectedIds.filter(item => item !== id)
      : [...state.selectedIds, id]
  })),
  clearSelection: () => set({ selectedIds: [] }),
  selectAll: () => set((state) => ({
    selectedIds: state.objects.map(obj => obj.id)
  })),
  setName: (name) => set({ name }),
  setDescription: (description) => set({ description }),
  setCardId: (id) => set({ cardId: id }),
  setOrgId: (orgId) => set({ orgId }),
  datasetId: '',
  datasetName: '',
  setDataSetId: (datasetId) => set({ datasetId }),
  setDataSetName: (datasetName) => set({ datasetName }),
  setCardSettings: (settings) => set({ cardSettings: settings }),

  // Grid
  toggleSnapToGrid: () => set((state) => ({
    isSnapToGridEnabled: !state.isSnapToGridEnabled
  })),
  setGridSize: (size) => set({ gridSize: size }),
  toggleGrid: () => set(state => ({
    showGrid: !state.showGrid
  })),

  // Aspect Ratio
  setAspectRatioLock: (isLocked) => set({ isAspectRatioLocked: isLocked }),

  resizeBackgroundsToCard: (cw, ch) => {
    set(state => {
      // ⬅️ ALWAYS read from the incoming state snapshot
      // const cw = state.cardWidth;
      // const ch = state.cardHeight;

      // Guard: never write invalid geometry
      if (!cw || !ch) {
        return {};
      }

      const resize = objs =>
        objs.map(o =>
          o.isBackground
            ? {
                ...o,
                x: 0,
                y: 0,
                width: cw,
                height: ch,
              }
            : o
        );

      return {
        objects: resize(state.objects),
        surfaces: Object.fromEntries(
          Object.entries(state.surfaces).map(([k, v]) => [
            k,
            resize(v),
          ])
        ),
      };
    });
  },



  // Object actions
  // IMPORTANT:
// objects is the live source of truth.
// NEVER rebuild objects from surfaces here.
addObject: (obj) => {
  set(state => {
    const normalized = {
      density: 1.0,
      ...obj
    };

    const hasBackground = state.objects.some(o => o.isBackground);

    let nextObjects;

    if (normalized.isBackground) {
      // 🔒 remove any existing background
      const nonBackgrounds = state.objects.filter(o => !o.isBackground);
      nextObjects = [normalized, ...nonBackgrounds];
    } else if (hasBackground) {
      // insert after background
      nextObjects = [
        state.objects[0],
        ...state.objects.slice(1),
        normalized
      ];
    } else {
      nextObjects = [...state.objects, normalized];
    }

    return {
      objects: nextObjects,
      selectedIds: [normalized.id],
      isDirty: true
    };
  });
},



  deleteObject: (id) =>
  set((state) => ({
    history: [...state.history, state.objects],
    objects: state.objects.filter(obj => obj.id !== id),
    selectedIds: state.selectedIds.filter(selId => selId !== id),
    redoStack: []
  })),
  getSelectedObject: () => {
      const state = get();
      if (state.selectedIds.length !== 1) return null;
      return state.objects.find(o => o.id === state.selectedIds[0]) || null;
    },


  updateObject: (id, updates) => set((state) => ({
    history: [...state.history, state.objects],
    objects: state.objects.map((obj) =>
      obj.id === id
        ? {
            ...obj,
            ...updates,
            dataBinding: updates.dataBinding
              ? { ...updates.dataBinding }
              : obj.dataBinding
          }
        : obj
    ),
    isDirty: true,
    redoStack: []
  })),
  deleteSelected: () => set((state) => ({
    history: [...state.history, state.objects],
    objects: state.objects.filter(obj => !state.selectedIds.includes(obj.id)),
    selectedIds: [],
    redoStack: []
  })),

  // Undo / Redo
  undo: () => set((state) => {
    if (state.history.length === 0) return state;
    const previous = state.history[state.history.length - 1];
    return {
      redoStack: [state.objects, ...state.redoStack],
      objects: previous,
      history: state.history.slice(0, -1),
      selectedIds: [],
    };
  }),
  redo: () => set((state) => {
    if (state.redoStack.length === 0) return state;
    const next = state.redoStack[0];
    return {
      history: [...state.history, state.objects],
      objects: next,
      redoStack: state.redoStack.slice(1),
      selectedIds: [],
    };
  }),

  // Layer control
  bringForward: () => set((state) => {
    if (state.selectedIds.length === 0) return {};

    const { bg, rest } = splitBackground(state.objects);
    let items = [...rest];

    // iterate from top → bottom to avoid double-swaps
    for (let i = items.length - 2; i >= 0; i--) {
      if (
        state.selectedIds.includes(items[i].id) &&
        !state.selectedIds.includes(items[i + 1].id)
      ) {
        [items[i], items[i + 1]] = [items[i + 1], items[i]];
      }
    }

    return {
      objects: bg ? [bg, ...items] : items
    };
  }),
  sendBackward: () => set((state) => {
    if (state.selectedIds.length === 0) return {};

    const { bg, rest } = splitBackground(state.objects);
    let items = [...rest];

    for (let i = 1; i < items.length; i++) {
      if (
        state.selectedIds.includes(items[i].id) &&
        !state.selectedIds.includes(items[i - 1].id)
      ) {
        [items[i - 1], items[i]] = [items[i], items[i - 1]];
      }
    }

    return {
      objects: bg ? [bg, ...items] : items
    };
  }),
  moveToTop: () => set((state) => {
    if (state.selectedIds.length === 0) return {};

    const { bg, rest } = splitBackground(state.objects);

    const selected = rest.filter(o => state.selectedIds.includes(o.id));
    const unselected = rest.filter(o => !state.selectedIds.includes(o.id));

    const reordered = [...unselected, ...selected];

    return {
      objects: bg ? [bg, ...reordered] : reordered
    };
  }),
  moveToBottom: () => set((state) => {
    if (state.selectedIds.length === 0) return {};

    const { bg, rest } = splitBackground(state.objects);

    const selected = rest.filter(o => state.selectedIds.includes(o.id));
    const unselected = rest.filter(o => !state.selectedIds.includes(o.id));

    const reordered = [...selected, ...unselected];

    return {
      objects: bg ? [bg, ...reordered] : reordered
    };
  }),

  // Clipboard
  // Clipboard
  copySelected: () => set((state) => {
    const copied = state.objects.filter(
      obj =>
        state.selectedIds.includes(obj.id) &&
        !isBackground(obj)
    );

    if (copied.length === 0) {
      return {}; // fail-safe: nothing copied
    }

    return {
      clipboard: copied.map(o => ({ ...o }))
    };
  }),

  cutSelected: () => set((state) => {
    const toCut = state.objects.filter(
      obj =>
        state.selectedIds.includes(obj.id) &&
        !isBackground(obj)
    );

    if (toCut.length === 0) return {};

    return {
      clipboard: toCut.map(o => ({ ...o })),
      objects: state.objects.filter(
        obj => !toCut.some(c => c.id === obj.id)
      ),
      selectedIds: [],
      isDirty: true
    };
  }),


    alignSelectedHorizontal: () => set((state) => {
    if (state.selectedIds.length < 2) return {};

    const selected = state.objects.filter(o => state.selectedIds.includes(o.id));
    const avgY =
      selected.reduce((sum, o) => sum + (o.y ?? 0), 0) / selected.length;

    return {
      history: [...state.history, state.objects],
      objects: state.objects.map(obj =>
        state.selectedIds.includes(obj.id)
          ? { ...obj, y: Math.round(avgY) }
          : obj
      ),
      redoStack: []
    };
  }),

  alignSelectedVertical: () => set((state) => {
    if (state.selectedIds.length < 2) return {};

    const selected = state.objects.filter(o => state.selectedIds.includes(o.id));
    const avgX =
      selected.reduce((sum, o) => sum + (o.x ?? 0), 0) / selected.length;

    return {
      history: [...state.history, state.objects],
      objects: state.objects.map(obj =>
        state.selectedIds.includes(obj.id)
          ? { ...obj, x: Math.round(avgX) }
          : obj
      ),
      redoStack: []
    };
  }),
  distributeSelectedHorizontal: () => set((state) => {
    if (state.selectedIds.length < 3) return {};

    const selected = state.objects
      .filter(o => state.selectedIds.includes(o.id))
      .sort((a, b) => (a.x ?? 0) - (b.x ?? 0));

    const first = selected[0];
    const last = selected[selected.length - 1];

    const startX = first.x ?? 0;
    const endX = last.x ?? 0;
    const step = (endX - startX) / (selected.length - 1);

    return {
      history: [...state.history, state.objects],
      objects: state.objects.map(obj => {
        const index = selected.findIndex(o => o.id === obj.id);
        if (index === -1) return obj;
        return {
          ...obj,
          x: Math.round(startX + step * index)
        };
      }),
      redoStack: []
    };
  }),
  distributeSelectedVertical: () => set((state) => {
    if (state.selectedIds.length < 3) return {};

    const selected = state.objects
      .filter(o => state.selectedIds.includes(o.id))
      .sort((a, b) => (a.y ?? 0) - (b.y ?? 0));

    const first = selected[0];
    const last = selected[selected.length - 1];

    const startY = first.y ?? 0;
    const endY = last.y ?? 0;
    const step = (endY - startY) / (selected.length - 1);

    return {
      history: [...state.history, state.objects],
      objects: state.objects.map(obj => {
        const index = selected.findIndex(o => o.id === obj.id);
        if (index === -1) return obj;
        return {
          ...obj,
          y: Math.round(startY + step * index)
        };
      }),
      redoStack: []
    };
  }),
  rotateSelected90: () => set((state) => ({
    history: [...state.history, state.objects],
    objects: state.objects.map(obj =>
      state.selectedIds.includes(obj.id)
        ? {
            ...obj,
            rotation: ((obj.rotation || 0) + 90) % 360
          }
        : obj
    ),
    redoStack: []
  })),

  pasteClipboard: () => set((state) => {
    const newObjects = state.clipboard.map((obj, i) => ({
      ...obj,
      id: `${obj.id}_copy_${Date.now() + i}`,
      x: obj.x + 20 * (i + 1),
      y: obj.y + 20 * (i + 1)
    }));
    return {
      history: [...state.history, state.objects],
      objects: [...state.objects, ...newObjects],
      selectedIds: newObjects.map(o => o.id),
      redoStack: []
    };
  }),

  // Movement
  nudgeSelected: (dx, dy) => set((state) => ({
    history: [...state.history, state.objects],
    objects: state.objects.map((obj) =>
      state.selectedIds.includes(obj.id) ? { ...obj, x: obj.x + dx, y: obj.y + dy } : obj
    ),
    redoStack: []
  })),

  // Menu / Tool UI
  activeMenu: null,
  setActiveMenu: (menu) => set({ activeMenu: menu }),
  activeTool: null,
  setActiveTool: (tool) => set({ activeTool: tool }),

  scale: 1,
  setScale: (scale) => set({ scale }),

  isTransforming: false,
  setIsTransforming: (v) => set({ isTransforming: v }),

  setActiveSurface: (nextKey) =>
    set((state) => {
      const next = normalizeSurfaceKey(nextKey);

      // stash current working objects
      const updatedSurfaces = {
        ...state.surfaces,
        [state.activeSurface]: state.objects.map(o => ({ ...o })),
      };

      return {
        surfaces: updatedSurfaces,
        activeSurface: next,
        objects: deepClone(updatedSurfaces[next] || []),
        selectedIds: [],
        history: [],
        redoStack: [],
      };
    }),

    // Load card from backend
    loadCard: (card) => {
      if (!card.objectsFront && card.objects) {
        console.warn(
          'Legacy card format detected: card.objects used instead of objectsFront'
        );
      }
      const surfaces = {
        front: deepClone(card.objectsFront ?? card.objects ?? []),
        frontSilver: deepClone(card.objectsFrontSilver ?? []),
        back: deepClone(card.objectsBack ?? []),
        backSilver: deepClone(card.objectsBackSilver ?? []),
      };

      const activeSurface = normalizeSurfaceKey(card.activeSurface ?? 'front');

      set({
        cardId: card.cardId ?? card._id ?? null,
        orgId: card.orgId ?? '',
        datasetId: card.datasetId ?? '',
        selectedTemplateId: card.templateId ?? null, // ✅ ADD THIS
        name: card.name ?? '',
        description: card.description ?? '',
        cardSettings: { ...card.cardSettings },
        surfaces,
        activeSurface,
        objects: deepClone(surfaces[activeSurface] || []),
        history: [],
        redoStack: [],
        selectedIds: [],
        isDirty: false,
        stageKey: Date.now(),
      });
    },
    setIsDirty: (dirty = true) =>
      set(() => ({
        isDirty: dirty
      })),

    // Return card payload for saving
    getCardPayload: () => {
      const state = get();

      const bakedSurfaces = {
        ...state.surfaces,
        [state.activeSurface]: state.objects.map(o => stripRuntimeProps(o)),
      };



      return {
        orgId: state.orgId,
        datasetId: state.datasetId,
        templateId: state.selectedTemplateId,
        name: state.name,
        description: state.description,
        cardSettings: state.cardSettings,

        objectsFront: bakedSurfaces.front.map(stripRuntimeProps),
        objectsFrontSilver: bakedSurfaces.frontSilver.map(stripRuntimeProps),
        objectsBack: bakedSurfaces.back.map(stripRuntimeProps),
        objectsBackSilver: bakedSurfaces.backSilver.map(stripRuntimeProps),
      };

    },

  resetCard: () =>
  set({
    cardId: null,
    orgId: '',
    name: '',
    description: '',
    datasetId: '',
    cardSettings: {
      size: 'cr80',
      format: 'landscape',
      display: 'Side 1',
      sides: 'Simplex',
      silverFront: 'no',
      silverBack: 'no',
    },
    surfaces: getDefaultSurfaces(),
    activeSurface: 'front',
    objects: [],
    selectedIds: [],
    history: [],
    redoStack: []
  }),

  updateCardSetting: (key, value) =>
    set((state) => {
      const prev = state.cardSettings;

      // 🔥 STEP 1: Bake current working objects into surfaces
      const bakedSurfaces = {
        ...state.surfaces,
        [state.activeSurface]: state.objects.map(o => ({ ...o })),
      };

      let nextSettings = {
        ...prev,
        [key]: value,
      };

      let nextSurfaces = { ...bakedSurfaces };
      let nextActiveSurface = state.activeSurface;

      // -----------------------------
      // 1️⃣ Duplex → Simplex
      // -----------------------------
      if (key === 'sides' && value === 'Simplex') {
        nextSurfaces.back = [];
        nextSurfaces.backSilver = [];

        nextSettings.silverBack = 'no';

        // 🔥 if user is currently on back, move them
        if (state.activeSurface === 'back' || state.activeSurface === 'backSilver') {
          nextActiveSurface = 'front';
        }
      }

      // -----------------------------
      // 2️⃣ Silver Front OFF
      // -----------------------------
      if (key === 'silverFront' && value === 'no') {
        nextSurfaces.frontSilver = [];

        if (state.activeSurface === 'frontSilver') {
          nextActiveSurface = 'front';
        }
      }

      // -----------------------------
      // 3️⃣ Silver Back OFF
      // -----------------------------
      if (key === 'silverBack' && value === 'no') {
        nextSurfaces.backSilver = [];

        if (state.activeSurface === 'backSilver') {
          nextActiveSurface = 'back';
        }
      }

      // 🔥 STEP 2: Load correct working surface
      const nextObjects = nextSurfaces[nextActiveSurface].map(o => ({ ...o }));

      return {
        cardSettings: nextSettings,
        surfaces: nextSurfaces,
        activeSurface: nextActiveSurface,
        objects: nextObjects,
        selectedIds: [],
        history: [],
        redoStack: [],
        isDirty: true,
      };
    }),

assignImagePlaceholder: (objectId, placeholder) => {
  set((state) => ({
    objects: state.objects.map((obj) =>
      obj.id === objectId
        ? { ...obj, imagePlaceholder: placeholder }
        : obj
    )
  }));
},

// showPlaceholderModal: false,
// setShowPlaceholderModal: (value) => set({ showPlaceholderModal: value }),

previewSrc: '',
setPreviewSrc: (src) => set({ previewSrc: src }),

fitMode: 'fit',
setFitMode: (mode) => set({ fitMode: mode }),

bindingModal: {
  open: false,
  targetId: null
},

openBindingModal: (id) =>
  set({ bindingModal: { open: true, targetId: id } }),

closeBindingModal: () =>
  set({ bindingModal: { open: false, targetId: null } }),

  commitSelectedObjectsFromStage: () => {
    const state = get();
    const surface = state.activeSurface;

    const committed = state.objects.map(o => ({ ...o }));

    set({
      surfaces: {
        ...state.surfaces,
        [surface]: committed
      },
      objects: committed
    });
  },

  copySelectedToSurface: (targetSurface) => {
    // 1) Flush live Konva transforms
    const stateBefore = get();
    if (typeof stateBefore.flushTransforms === 'function') {
      stateBefore.flushTransforms();
    }

    const state = get();
    const target = targetSurface;

    if (!state.surfaces?.[target]) return;
    if (!state.selectedIds?.length) return;

    // 2) Bake current working objects into active surface
    const bakedSurfaces = {
      ...state.surfaces,
      [state.activeSurface]: state.objects.map(o => ({ ...o })),
    };

    const source = bakedSurfaces[state.activeSurface] || [];
    const selected = source.filter(o => state.selectedIds.includes(o.id));
    if (!selected.length) return;

    // 🔥 helper: convert to silver-safe object
    const toSilver = (obj) => {
      const copy = structuredClone(obj);

      // ❌ REMOVE ALL DATA BINDING
      delete copy.dataBinding;
      delete copy.textBinding;
      delete copy.fieldKey;
      delete copy.fieldId;
      delete copy.mapSlot;
      delete copy.mapLabel;

      // 🔥 PHOTO → RECT CONVERSION
      if (copy.type === 'photo') {
        return {
          id: generateId(),
          type: 'rect',

          x: copy.x,
          y: copy.y,
          width: copy.width,
          height: copy.height,
          rotation: copy.rotation || 0,

          cornerRadius: copy.cornerRadius || 0,

          fill: '#000000',
          stroke: null,
          strokeWidth: 0,

          opacity: copy.opacity ?? 1,

          draggable: copy.draggable,

          isSilver: true,
        };
      }

      // ✅ NON-PHOTO OBJECTS

      // Force fill to black
      if (copy.fill && copy.fill !== 'transparent') {
        copy.fill = '#000000';
      }

      // Force stroke to black
      if (copy.stroke && copy.stroke !== 'transparent') {
        copy.stroke = '#000000';
      }

      // Remove image-related props just in case
      delete copy.imagePlaceholder;
      delete copy.src;
      delete copy.originalSrc;

      copy.isSilver = true;

      return copy;
    };

    // 3) Create copies
    const copies = selected.map(o => ({
      ...toSilver(o),
      id: generateId(),
    }));

    // 4) Append to target surface
    set({
      surfaces: {
        ...bakedSurfaces,
        [target]: [...(bakedSurfaces[target] || []), ...copies],
      },
    });
  },

}));

export default useDesignerStore;
